
import { useCallback, useMemo } from 'react';
import { User, BirdieRecord, FriendWithStatus, BirdieGameGroup } from '@/types';

export interface BirdieLeader extends User {
  birdieCount: number;
  status?: 'pending' | 'accepted' | 'rejected' | 'invited';
  isRequester?: boolean;
}

export function useBirdieData(
  users: User[],
  birdieRecords: BirdieRecord[],
  initialYear: number = new Date().getFullYear()
) {
  // Create stable keys for memoization
  const stableUserIds = useMemo(() =>
    users.map(user => user.id).sort(),
    [users]
  );

  const stableRecordIds = useMemo(() =>
    birdieRecords.map(record => record.id).sort(),
    [birdieRecords]
  );

  // Filter records by year
  const filteredBirdieRecords = useMemo(() => {
    return birdieRecords.filter(record => {
      const recordYear = new Date(record.date).getFullYear();
      return recordYear === initialYear;
    });
  }, [birdieRecords, initialYear, stableRecordIds.join(',')]);

  // Memoize user birdie counts map
  const userBirdieCounts = useMemo(() => {
    const counts = new Map<string, number>();

    for (const record of filteredBirdieRecords) {
      const userId = String(record.userId);
      counts.set(userId, (counts.get(userId) || 0) + 1);
    }

    return counts;
  }, [filteredBirdieRecords]);

  // Get total birdies for a specific user in the selected year
  const getUserYearBirdies = useCallback((userId: string): number => {
    if (!userId) return 0;
    return userBirdieCounts.get(userId) || 0;
  }, [userBirdieCounts]);

  // Calculate birdie leaders
  const getBirdieLeaders = useCallback((limit?: number): BirdieLeader[] => {
    const leaders = users
      .map(user => {
        const friendWithStatus = user as FriendWithStatus;
        const status = friendWithStatus.status;
        const isRequester = friendWithStatus.isRequester;

        const birdieCount = userBirdieCounts.get(user.id) || 0;

        return {
          ...user,
          birdieCount,
          status,
          isRequester
        };
      })
      .sort((a, b) => b.birdieCount - a.birdieCount);

    return limit ? leaders.slice(0, limit) : leaders;
  }, [users, userBirdieCounts, stableUserIds.join(',')]);

  // Filter games by user participation
  const filterUserGames = useCallback((games: BirdieGameGroup[], userId: string): BirdieGameGroup[] => {
    return games.filter(game =>
      game.members.some(member => member.id === userId)
    ).sort((a, b) => b.year - a.year);
  }, []);

  return {
    selectedYear: initialYear,
    getUserYearBirdies,
    getBirdieLeaders,
    filterUserGames
  };
}
